import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Wisecall Landing Page - Mobile-First, Conversion-Optimized
export function LandingPage() {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: "🎯",
      title: "Nie wieder Anrufe verpassen",
      desc: "Ihr KI-Assistent nimmt jeden Anruf an - 24/7, 365 Tage im Jahr. Auch nachts, am Wochenende und im Urlaub."
    },
    {
      icon: "🧠",
      title: "Intelligent wie ein Mitarbeiter",
      desc: "Beantwortet Fragen, vereinbart Termine, qualifiziert Anfragen - basierend auf Ihrem Wissen und Ihren Regeln."
    },
    {
      icon: "⚡",
      title: "Sofort einsatzbereit",
      desc: "In 5 Minuten eingerichtet. Keine IT-Kenntnisse nötig. Einfach Telefonnummer verbinden und loslegen."
    },
    {
      icon: "🔗",
      title: "Automatische Aktionen",
      desc: "Sendet E-Mails, SMS, bucht Termine in Ihrem Kalender - alles automatisch während des Gesprächs."
    },
    {
      icon: "🇩🇪",
      title: "Perfektes Deutsch",
      desc: "Natürliche Gespräche auf Deutsch. Kein Roboter-Feeling. Ihre Anrufer merken keinen Unterschied."
    },
    {
      icon: "📊",
      title: "Volle Transparenz",
      desc: "Transkripte, Aufnahmen und Analysen für jeden Anruf. Sie wissen immer, was besprochen wurde."
    }
  ];

  const useCases = [
    {
      industry: "Arztpraxen",
      icon: "🏥",
      problem: "Dauerklingeln, genervte Patienten, gestresstes Personal",
      solution: "Wisecall nimmt Terminanfragen an, prüft Verfügbarkeit und bestätigt per SMS",
      result: "70% weniger Telefonzeit"
    },
    {
      industry: "Handwerker",
      icon: "🔧",
      problem: "Aufträge gehen verloren während Sie arbeiten",
      solution: "Wisecall nimmt Anfragen auf, qualifiziert sie und leitet wichtige Fälle weiter",
      result: "30% mehr Aufträge"
    },
    {
      industry: "Rechtsanwälte",
      icon: "⚖️",
      problem: "Erstberatungen binden Kanzleizeit",
      solution: "Wisecall führt strukturierte Erstgespräche und sammelt alle relevanten Infos",
      result: "2h gespart pro Mandat"
    },
    {
      industry: "Immobilien",
      icon: "🏠",
      problem: "Interessenten rufen abends und am Wochenende",
      solution: "Wisecall beantwortet Fragen zum Objekt und vereinbart Besichtigungstermine",
      result: "24/7 erreichbar"
    }
  ];

  const testimonials = [
    {
      quote: "Seit Wisecall verpassen wir keinen Anruf mehr. Unsere Patienten sind begeistert von der schnellen Terminvergabe.",
      author: "Dr. Sarah M.",
      role: "Zahnärztin, München",
      avatar: "👩‍⚕️"
    },
    {
      quote: "Endlich kann ich auf der Baustelle arbeiten ohne ständig ans Telefon zu müssen. Die Auftragsanfragen kommen trotzdem rein.",
      author: "Thomas K.",
      role: "Elektrikermeister, Hamburg",
      avatar: "👷"
    },
    {
      quote: "Die Qualität der Erstgespräche hat sich massiv verbessert. Wisecall sammelt alle Infos, die wir brauchen.",
      author: "Julia R.",
      role: "Rechtsanwältin, Berlin",
      avatar: "👩‍💼"
    }
  ];

  const faqs = [
    {
      q: "Wie natürlich klingt die KI-Stimme?",
      a: "Wisecall nutzt die neueste OpenAI-Technologie. Die Stimme klingt natürlich und menschlich - Ihre Anrufer werden keinen Unterschied bemerken. Sie können aus verschiedenen Stimmen wählen."
    },
    {
      q: "Kann ich Wisecall mit meiner bestehenden Telefonnummer nutzen?",
      a: "Ja! Sie können Ihre bestehende Nummer per Rufumleitung verbinden oder eine neue Nummer von uns bekommen. Die Einrichtung dauert nur wenige Minuten."
    },
    {
      q: "Was passiert, wenn die KI eine Frage nicht beantworten kann?",
      a: "Wisecall ist ehrlich und sagt, dass es die Frage notiert und Sie zurückrufen werden. Optional kann der Anruf auch direkt an Sie weitergeleitet werden."
    },
    {
      q: "Ist Wisecall DSGVO-konform?",
      a: "Absolut. Alle Daten werden in deutschen Rechenzentren gespeichert. Wir erfüllen alle DSGVO-Anforderungen und bieten AV-Verträge an."
    },
    {
      q: "Wie schnell ist Wisecall einsatzbereit?",
      a: "Nach der Registrierung können Sie Ihren ersten KI-Agenten in unter 5 Minuten einrichten. Telefonnummer verbinden, Prompt eingeben, fertig."
    },
    {
      q: "Was kostet Wisecall?",
      a: "Wir bieten einen kostenlosen Plan mit 60 Minuten/Monat zum Testen. Danach ab 29€/Monat für 180 Minuten. Keine versteckten Kosten, keine Einrichtungsgebühr."
    }
  ];

  const plans = [
    {
      name: "Free",
      price: "0",
      period: "für immer",
      features: ["1 KI-Agent", "60 Minuten/Monat", "Email & SMS Tools", "Wissensbasis"],
      cta: "Kostenlos starten",
      popular: false
    },
    {
      name: "Starter",
      price: "29",
      period: "/Monat",
      features: ["3 KI-Agenten", "180 Minuten/Monat", "Alle Tools", "Prioritäts-Support"],
      cta: "Jetzt starten",
      popular: false
    },
    {
      name: "Pro",
      price: "79",
      period: "/Monat",
      features: ["10 KI-Agenten", "500 Minuten/Monat", "Alle Tools", "API-Zugang", "Prioritäts-Support"],
      cta: "Jetzt starten",
      popular: true
    },
    {
      name: "Business",
      price: "199",
      period: "/Monat",
      features: ["Unbegrenzte Agenten", "2000 Minuten/Monat", "Alle Tools", "Dedizierter Support", "SLA-Garantie"],
      cta: "Kontakt aufnehmen",
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <div className="flex items-center">
              <img src="/wisecall-logo.png" alt="Wisecall" className="h-12 w-auto" />
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 transition-colors">Features</a>
              <a href="#usecases" className="text-gray-600 hover:text-gray-900 transition-colors">Branchen</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 transition-colors">Preise</a>
              <a href="#faq" className="text-gray-600 hover:text-gray-900 transition-colors">FAQ</a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <button 
                onClick={() => navigate('/login')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 font-medium transition-colors"
              >
                Anmelden
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-full hover:shadow-lg hover:shadow-emerald-500/25 transition-all"
              >
                Kostenlos testen
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-600"
            >
              {isMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block py-2 text-gray-600">Features</a>
              <a href="#usecases" className="block py-2 text-gray-600">Branchen</a>
              <a href="#pricing" className="block py-2 text-gray-600">Preise</a>
              <a href="#faq" className="block py-2 text-gray-600">FAQ</a>
              <hr />
              <button 
                onClick={() => navigate('/login')}
                className="w-full py-2.5 text-gray-700 font-medium rounded-xl border border-gray-200 hover:bg-gray-50 transition-colors"
                data-testid="mobile-login-btn"
              >
                Anmelden
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-xl"
                data-testid="mobile-register-btn"
              >
                Kostenlos testen
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/50 to-white"></div>
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute top-40 right-1/4 w-72 h-72 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-pulse" style={{animationDelay: '1s'}}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-6">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              Jetzt mit GPT-4 Turbo Technologie
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Ihr KI-Telefonassistent.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-600">
                24/7 erreichbar.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Wisecall beantwortet Anrufe, vereinbart Termine und qualifiziert Anfragen - 
              während Sie sich auf Ihr Geschäft konzentrieren.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
              <button 
                onClick={() => navigate('/login')}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-full text-lg hover:shadow-xl hover:shadow-emerald-500/25 transition-all transform hover:-translate-y-0.5"
              >
                Kostenlos testen
              </button>
              <a 
                href="#demo"
                className="w-full sm:w-auto px-8 py-4 bg-white text-gray-700 font-semibold rounded-full text-lg border-2 border-gray-200 hover:border-gray-300 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
                Demo anhören
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Keine Kreditkarte nötig
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                In 5 Minuten startklar
              </div>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                DSGVO-konform
              </div>
            </div>
          </div>

          {/* Hero Image / Mockup */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden mx-auto max-w-5xl">
              <div className="p-2 sm:p-3 flex gap-1.5 sm:gap-2">
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></div>
                <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500"></div>
              </div>
              <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-4 sm:p-8">
                {/* Chat Demo */}
                <div className="max-w-lg mx-auto space-y-4">
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm">W</div>
                    <div className="bg-gray-700 rounded-2xl rounded-tl-none px-4 py-3 text-gray-100">
                      Guten Tag, Zahnarztpraxis Dr. Müller, mein Name ist Lisa. Wie kann ich Ihnen helfen?
                    </div>
                  </div>
                  <div className="flex gap-3 items-start justify-end">
                    <div className="bg-emerald-500 rounded-2xl rounded-tr-none px-4 py-3 text-white">
                      Hallo, ich hätte gerne einen Termin für eine Kontrolle.
                    </div>
                    <div className="w-8 h-8 rounded-full bg-gray-600 flex items-center justify-center text-white text-sm">A</div>
                  </div>
                  <div className="flex gap-3 items-start">
                    <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm">W</div>
                    <div className="bg-gray-700 rounded-2xl rounded-tl-none px-4 py-3 text-gray-100">
                      Sehr gerne! Ich habe am Donnerstag um 14:30 Uhr oder Freitag um 10:00 Uhr noch Termine frei. Was passt Ihnen besser?
                    </div>
                  </div>
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                    <span className="text-emerald-400 text-sm">Live-Gespräch läuft...</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating Elements */}
            <div className="absolute -left-4 sm:left-8 top-1/4 bg-white rounded-xl shadow-xl p-3 sm:p-4 hidden sm:block animate-bounce" style={{animationDuration: '3s'}}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Termin gebucht!</p>
                  <p className="text-sm text-gray-500">SMS Bestätigung gesendet</p>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 sm:right-8 bottom-1/4 bg-white rounded-xl shadow-xl p-3 sm:p-4 hidden sm:block animate-bounce" style={{animationDuration: '3.5s'}}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-gray-900">Transkript bereit</p>
                  <p className="text-sm text-gray-500">Gespräch wurde gespeichert</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof - Logos */}
      <section className="py-12 sm:py-16 border-y border-gray-100 bg-gray-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 mb-8">Vertraut von über 500+ Unternehmen in Deutschland</p>
          <div className="flex flex-wrap items-center justify-center gap-8 sm:gap-16 opacity-60 grayscale">
            <div className="text-2xl font-bold text-gray-400">Praxis+</div>
            <div className="text-2xl font-bold text-gray-400">MediTech</div>
            <div className="text-2xl font-bold text-gray-400">HandwerkPro</div>
            <div className="text-2xl font-bold text-gray-400">ImmoCare</div>
            <div className="text-2xl font-bold text-gray-400">LegalFirst</div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Alles, was Ihr Telefon-Assistent braucht
            </h2>
            <p className="text-lg text-gray-600">
              Wisecall ist mehr als ein Anrufbeantworter. Es ist Ihr intelligenter Mitarbeiter am Telefon.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 hover:border-emerald-200 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-2xl flex items-center justify-center text-2xl mb-5 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section id="usecases" className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Für jede Branche die richtige Lösung
            </h2>
            <p className="text-lg text-gray-600">
              Egal ob Arztpraxis, Handwerker oder Anwaltskanzlei - Wisecall passt sich Ihren Anforderungen an.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
            {useCases.map((useCase, i) => (
              <div 
                key={i}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center text-2xl text-white">
                    {useCase.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">{useCase.industry}</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium text-red-500 mb-1">Problem:</p>
                    <p className="text-gray-600">{useCase.problem}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-emerald-500 mb-1">Lösung:</p>
                    <p className="text-gray-600">{useCase.solution}</p>
                  </div>
                  <div className="pt-4 border-t border-gray-100">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full font-semibold">
                      {useCase.result}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Was unsere Kunden sagen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {testimonials.map((testimonial, i) => (
              <div 
                key={i}
                className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 sm:p-8 text-white"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-5 h-5 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center text-2xl">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-gray-400">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Einfache, transparente Preise
            </h2>
            <p className="text-lg text-gray-600">
              Starten Sie kostenlos. Upgraden Sie, wenn Sie wachsen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {plans.map((plan, i) => (
              <div 
                key={i}
                className={`relative bg-white rounded-2xl p-6 sm:p-8 border-2 transition-all ${
                  plan.popular 
                    ? 'border-emerald-500 shadow-xl shadow-emerald-500/10' 
                    : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-emerald-500 to-teal-600 text-white text-sm font-bold rounded-full">
                    BELIEBT
                  </div>
                )}
                
                <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}€</span>
                  <span className="text-gray-500">{plan.period}</span>
                </div>
                
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-gray-600">
                      <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => navigate('/login')}
                  className={`w-full py-3 rounded-xl font-semibold transition-all ${
                    plan.popular
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:shadow-lg hover:shadow-emerald-500/25'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Häufige Fragen
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div 
                key={i}
                className="bg-white rounded-xl border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900">{faq.q}</span>
                  <svg 
                    className={`w-5 h-5 text-gray-500 transition-transform ${activeFaq === i ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {activeFaq === i && (
                  <div className="px-6 pb-4 text-gray-600">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-3xl p-8 sm:p-16 text-center relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-40 h-40 border-2 border-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
              <div className="absolute bottom-0 right-0 w-60 h-60 border-2 border-white rounded-full translate-x-1/2 translate-y-1/2"></div>
            </div>
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Bereit, jeden Anruf zu gewinnen?
              </h2>
              <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
                Starten Sie noch heute kostenlos und erleben Sie, wie Wisecall Ihr Geschäft verändert.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button 
                  onClick={() => navigate('/login')}
                  className="w-full sm:w-auto px-8 py-4 bg-white text-emerald-600 font-bold rounded-full text-lg hover:shadow-xl transition-all"
                >
                  Kostenlos starten - 60 Min. gratis
                </button>
                <a 
                  href="mailto:kontakt@wisecall.de"
                  className="w-full sm:w-auto px-8 py-4 bg-transparent text-white font-semibold rounded-full text-lg border-2 border-white/50 hover:bg-white/10 transition-all"
                >
                  Demo vereinbaren
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-4">
                <img src="/wisecall-logo-white.png" alt="Wisecall" className="h-10 w-auto" />
              </div>
              <p className="text-sm">Der intelligente KI-Telefonassistent für Ihr Unternehmen.</p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Produkt</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Preise</a></li>
                <li><a href="#usecases" className="hover:text-white transition-colors">Branchen</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Unternehmen</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Über uns</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Karriere</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Kontakt</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Rechtliches</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Impressum</a></li>
                <li><a href="/datenschutz" className="hover:text-white transition-colors">Datenschutz</a></li>
                <li><a href="/nutzungsbedingungen" className="hover:text-white transition-colors">Nutzungsbedingungen</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm">© 2025 Wisecall. Alle Rechte vorbehalten.</p>
            <div className="flex items-center gap-2">
              <span className="text-sm">Made with</span>
              <span className="text-red-500">❤️</span>
              <span className="text-sm">in Germany</span>
              <span className="ml-2">🇩🇪</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
